import { matchPath, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import { KeepInitialLoader } from "@/components/common/InitialLoaderHider";
import ChatWidget, {
  type ChatMessage,
  type ServiceRecommendation,
} from "@/components/common/ChatWidget";
import { PageContextProvider, usePageContext } from "@/hooks/usePageContext";
import { discoverPublicContent } from "@/services/publicDiscoveryService";

/**
 * Whether the path is a public application form that shows the step's guide:
 * a service's slug form (`/services/:serviceSlug/apply`, App.tsx) or an
 * unlisted service's form through its link (`/s/:token/apply`). The CAPE form
 * has a route of its own that the slug pattern also matches, and React Router
 * renders it instead, so it is left out here as well. Read from the route, so
 * it holds from first paint, before the lazy page sets its page context; a
 * link's form never says "application-form" (an unlisted service stays out of
 * every AI answer, so its context is only `{ page: "other" }`).
 */
function isPublicApplicationFormPath(pathname: string): boolean {
  const slugForm = matchPath("/services/:serviceSlug/apply", pathname);
  if (slugForm) return slugForm.params.serviceSlug !== "cape-registration";
  return matchPath("/s/:token/apply", pathname) !== null;
}

/**
 * Inner layout that reads page context and passes it to the AI
 */
function PublicLayoutInner() {
  const { pageContext } = usePageContext();
  const { t, i18n } = useTranslation("public");
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const onApplicationForm = isPublicApplicationFormPath(location.pathname);

  useEffect(() => {
    const handler = () => setIsAssistantOpen(true);
    window.addEventListener("moe:open-chat", handler);
    return () => window.removeEventListener("moe:open-chat", handler);
  }, []);

  // Reset scroll position on route change so the page transition starts from the top
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  const handleChatMessage = useCallback(
    async (
      message: string,
      history: ChatMessage[],
    ): Promise<{
      content: string;
      recommendations?: ServiceRecommendation[];
    }> => {
      const conversationHistory = history
        .slice(-8)
        .map((m) => ({ role: m.role, content: m.content }));

      const result = await discoverPublicContent({
        message,
        conversationHistory,
        pageContext,
        experience: "chat",
        language: currentLanguage,
      });

      return {
        content: result.content,
        recommendations: result.recommendations as ServiceRecommendation[],
      };
    },
    [currentLanguage, pageContext],
  );

  const suggestions = useMemo(() => {
    if (pageContext.page === "service-detail" && pageContext.serviceName) {
      return [
        t("assistant.suggestions.serviceDetailRequirements", {
          serviceName: pageContext.serviceName,
        }),
        t("assistant.suggestions.serviceDetailProcessing", {
          serviceName: pageContext.serviceName,
        }),
        t("assistant.suggestions.documents"),
      ];
    }
    if (pageContext.page === "application-form" && pageContext.serviceName) {
      return [
        t("assistant.suggestions.formHelp"),
        t("assistant.suggestions.fieldHelp"),
        t("assistant.suggestions.formDocuments"),
      ];
    }
    if (pageContext.page === "services") {
      return [
        t("assistant.suggestions.transferService"),
        t("assistant.suggestions.examResults"),
        t("assistant.suggestions.downloads"),
      ];
    }
    if (pageContext.page === "help") {
      return [
        t("assistant.suggestions.transferDocuments"),
        t("assistant.suggestions.trackApplication"),
        t("assistant.suggestions.downloadableForms"),
      ];
    }
    return [
      t("assistant.suggestions.enrollChild"),
      t("assistant.suggestions.copyExamResults"),
      t("assistant.suggestions.transferChild"),
    ];
  }, [pageContext, t]);

  const subtitle = useMemo(() => {
    if (pageContext.serviceName) {
      return t("assistant.subtitleViewing", {
        serviceName: pageContext.serviceName,
      });
    }
    return t("assistant.subtitleDefault");
  }, [pageContext.serviceName, t]);

  const handleHeaderSearch = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      setIsAssistantOpen(false);
      navigate(`/services?search=${encodeURIComponent(trimmed)}`);
    },
    [navigate],
  );

  return (
    <div className="min-h-screen flex flex-col bg-light-gray">
      <Header onAiSearch={handleHeaderSearch} />
      {/*
        While a page's chunk downloads, the layout commits around an empty
        <main>. KeepInitialLoader holds the splash over it until the page
        replaces the fallback (see InitialLoaderHider). min-h-screen keeps the
        footer below the first screen meanwhile, so the page's arrival, and
        later its data's, cannot move the footer in view.
      */}
      <main className="public-page-shell flex-1 min-h-screen pt-24">
        <Suspense fallback={<KeepInitialLoader />}>
          <div
            key={location.pathname}
            className="animate-in fade-in-0 slide-in-from-bottom-2 duration-500 ease-out motion-reduce:animate-none"
          >
            <Outlet />
          </div>
        </Suspense>
      </main>
      <Footer />
      {/* Not on the application form: its guide sits beside the questions,
          with its own chat under "Still stuck?", and on a phone the form's
          bottom bar is where this button would float. */}
      {!onApplicationForm && pageContext.page !== "application-form" && (
        <ChatWidget
          mode="floating"
          title={t("assistant.serviceTitle")}
          subtitle={subtitle}
          placeholder={t("assistant.placeholder")}
          welcomeMessage={t("assistant.welcomeMessage")}
          suggestions={suggestions}
          onSendMessage={handleChatMessage}
          isOpen={isAssistantOpen}
          onOpenChange={setIsAssistantOpen}
        />
      )}
    </div>
  );
}

/**
 * PublicLayout - Layout wrapper for public-facing pages
 * Includes the main navigation header, footer, and AI service discovery chatbot
 */
function PublicLayout() {
  return (
    <PageContextProvider>
      <PublicLayoutInner />
    </PageContextProvider>
  );
}

export default PublicLayout;

/**
 * PublicLayoutNoFooter — same as PublicLayout but without the site footer.
 * Used for full-screen form pages (e.g. CAPE application) where the footer
 * would appear mid-scroll beneath the sticky action bar.
 */
function PublicLayoutNoFooterInner() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("public");
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const { pageContext } = usePageContext();

  const handleChatMessage = useCallback(
    async (
      message: string,
      history: ChatMessage[],
    ): Promise<{
      content: string;
      recommendations?: ServiceRecommendation[];
    }> => {
      const conversationHistory = history
        .slice(-8)
        .map((m) => ({ role: m.role, content: m.content }));

      const result = await discoverPublicContent({
        message,
        conversationHistory,
        pageContext,
        experience: "chat",
        language: currentLanguage,
      });

      return {
        content: result.content,
        recommendations: result.recommendations as ServiceRecommendation[],
      };
    },
    [currentLanguage, pageContext],
  );

  const handleHeaderSearch = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      setIsAssistantOpen(false);
      navigate(`/services?search=${encodeURIComponent(trimmed)}`);
    },
    [navigate],
  );

  return (
    <div className="h-screen flex flex-col bg-light-gray overflow-hidden">
      <Header onAiSearch={handleHeaderSearch} />
      <main className="public-page-shell flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </main>
      <ChatWidget
        mode="floating"
        title={t("assistant.serviceTitle")}
        subtitle={t("assistant.subtitleDefault")}
        placeholder={t("assistant.placeholder")}
        welcomeMessage={t("assistant.welcomeMessage")}
        suggestions={[]}
        onSendMessage={handleChatMessage}
        isOpen={isAssistantOpen}
        onOpenChange={setIsAssistantOpen}
      />
    </div>
  );
}

export function PublicLayoutNoFooter() {
  return (
    <PageContextProvider>
      <PublicLayoutNoFooterInner />
    </PageContextProvider>
  );
}
